import React, { useState, useEffect, useContext } from 'react';
import { InfinitySpin } from 'react-loader-spinner';
import Wrapper from '../../Components/Wrapper';
import { TabPane, Tabs } from '../../Components/Tabs';
import Grid from '../../Components/Grid';
import styles from './ManagementLauncher.module.css';
import LauncherAPI from '../../Networking/LauncherAPI';

interface CheatSignature {
  id: number;
  file_name: string;
  file_hash: string;
  signature_type: string;
  block_type: 'blacklist' | 'whitelist';
  file_size: number;
  uploaded_by: number;
  upload_time: string;
}

interface LauncherSession {
  id: string;
  username: string;
  hwid: string;
  ipAddress: string;
  connectedAt: string;
  lastActivity: string;
  status: 'active' | 'idle' | 'suspicious';
}

interface FileCheck {
  name: string;
  hash: string;
  usageCount: number;
  flagged: boolean;
  lastSeen: string;
}

interface ProcessInfo {
  name: string;
  pid: number;
  path: string;
  flagged: boolean;
}

interface ModelFile {
  name: string;
  size: number;
}

interface FeedItem {
  id: number;
  type: 'news' | 'changelog';
  category: string | null;
  text: string;
  version: string | null;
  timestamp: string;
}

interface NewsItem {
  id: number;
  category: string;
  description: string;
  created_at: string;
}

interface ChangelogItem {
  id: number;
  discord_message_id: string;
  version: string;
  text: string;
  discord_timestamp: string;
  created_at: string;
  updated_at: string;
}

const ManagementLauncher: React.FC = () => {
  // Signatures State
  const [signatures, setSignatures] = useState<CheatSignature[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [blockTypeFilter, setBlockTypeFilter] = useState<string>('all');
  const [signatureTypeFilter, setSignatureTypeFilter] = useState<string>('all');
  const [uploadProgress, setUploadProgress] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Manual Add State
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [manualFileName, setManualFileName] = useState('');
  const [manualFileHash, setManualFileHash] = useState('');
  const [manualSignatureType, setManualSignatureType] = useState('file');
  const [manualBlockType, setManualBlockType] = useState<'blacklist' | 'whitelist'>('blacklist');
  const [manualFileSize, setManualFileSize] = useState('');
  const [manualAddLoading, setManualAddLoading] = useState(false);

  // File Upload state (type/block chosen before uploading)
  const [uploadSignatureType, setUploadSignatureType] = useState('file');
  const [uploadBlockType, setUploadBlockType] = useState<'blacklist' | 'whitelist'>('blacklist');

  // Sessions State
  const [sessions, setSessions] = useState<LauncherSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [selectedSession, setSelectedSession] = useState<string | null>(null);

  // File Monitoring State
  const [fileName, setFileName] = useState('');
  const [fileHash, setFileHash] = useState('');
  const [fileCheckResult, setFileCheckResult] = useState<FileCheck | null>(null);
  const [fileCheckLoading, setFileCheckLoading] = useState(false);

  // Screenshot State
  const [screenshotSessionId, setScreenshotSessionId] = useState('');
  const [screenshotData, setScreenshotData] = useState<string | null>(null);
  const [screenshotLoading, setScreenshotLoading] = useState(false);

  // Process Monitor State
  const [processSessionId, setProcessSessionId] = useState('');
  const [processes, setProcesses] = useState<ProcessInfo[]>([]);
  const [processesLoading, setProcessesLoading] = useState(false);

  // Feed & News State
  const [feed, setFeed] = useState<FeedItem[]>([]);
  const [feedLoading, setFeedLoading] = useState(false);
  const [news, setNews] = useState<NewsItem[]>([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [changelogs, setChangelogs] = useState<ChangelogItem[]>([]);
  const [changelogsLoading, setChangelogsLoading] = useState(false);
  const [discordMembers, setDiscordMembers] = useState<number | null>(null);
  const [newsCategory, setNewsCategory] = useState('announcement');
  const [newsDescription, setNewsDescription] = useState('');
  const [postNewsLoading, setPostNewsLoading] = useState(false);
  const [editingNewsId, setEditingNewsId] = useState<number | null>(null);
  const [editCategory, setEditCategory] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editNewsLoading, setEditNewsLoading] = useState(false);

  // Models State
  const [models, setModels] = useState<ModelFile[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelUploadFile, setModelUploadFile] = useState<File | null>(null);
  const [modelUploadProgress, setModelUploadProgress] = useState(false);
  const [modelError, setModelError] = useState<string | null>(null);
  const [modelPreview, setModelPreview] = useState<{ name: string; url: string } | null>(null);

  const ITEMS_PER_PAGE = 15;
  const launcherAPI = new LauncherAPI();

  const mockSignatures: CheatSignature[] = [
    {
      id: 1,
      file_name: 'sobeit.dll',
      file_hash: 'a1b2c3d4e5f6789abcdef',
      signature_type: 'file',
      block_type: 'blacklist',
      file_size: 204800,
      uploaded_by: 1,
      upload_time: '2024-01-15T10:30:00Z'
    },
    {
      id: 2,
      file_name: 'cheatengine.exe',
      file_hash: 'f6e5d4c3b2a1098765fedcba',
      signature_type: 'file',
      block_type: 'blacklist',
      file_size: 5242880,
      uploaded_by: 1,
      upload_time: '2024-01-20T14:20:00Z'
    },
    {
      id: 3,
      file_name: 'speedhack.asi',
      file_hash: '1a2b3c4d5e6f7890abcdef12',
      signature_type: 'module',
      block_type: 'blacklist',
      file_size: 0,
      uploaded_by: 2,
      upload_time: '2024-01-22T09:15:00Z'
    },
  ];

  // Feed & News Functions
  const fetchFeed = async () => {
    setFeedLoading(true);
    try {
      const response = await launcherAPI.getFeed();
      if (response && response.success && Array.isArray(response.data)) {
        setFeed(response.data);
      } else {
        setFeed([]);
      }
    } catch (err) {
      console.error('Failed to fetch feed:', err);
      setFeed([]);
    } finally {
      setFeedLoading(false);
    }
  };

  const fetchNews = async () => {
    setNewsLoading(true);
    try {
      const response = await launcherAPI.getNews();
      if (response && response.success && Array.isArray(response.data)) {
        setNews(response.data);
      } else {
        setNews([]);
      }
    } catch (err) {
      console.error('Failed to fetch news:', err);
      setNews([]);
    } finally {
      setNewsLoading(false);
    }
  };

  const fetchChangelogs = async () => {
    setChangelogsLoading(true);
    try {
      const response = await launcherAPI.getChangelogs();
      if (response && response.success && Array.isArray(response.data)) {
        setChangelogs(response.data);
      } else {
        setChangelogs([]);
      }
    } catch (err) {
      console.error('Failed to fetch changelogs:', err);
      setChangelogs([]);
    } finally {
      setChangelogsLoading(false);
    }
  };

  const fetchDiscordMembers = async () => {
    try {
      const response = await launcherAPI.getDiscordMembers();
      if (response && response.success && response.data) {
        setDiscordMembers(response.data.discord_members);
      }
    } catch (err) {
      console.error('Failed to fetch discord members:', err);
    }
  };

  const handlePostNews = async () => {
    if (!newsDescription.trim()) return;
    setPostNewsLoading(true);
    try {
      const response = await launcherAPI.postNews(newsCategory, newsDescription);
      if (response && response.success) {
        setNewsDescription('');
        await fetchNews();
        await fetchFeed();
      } else {
        alert(response?.error ?? 'Failed to post news');
      }
    } catch (err) {
      console.error('Failed to post news:', err);
      alert('Failed to post news');
    } finally {
      setPostNewsLoading(false);
    }
  };

  const startEditNews = (id: number, category: string, description: string) => {
    setEditingNewsId(id);
    setEditCategory(category);
    setEditDescription(description);
  };

  const cancelEditNews = () => {
    setEditingNewsId(null);
    setEditCategory('');
    setEditDescription('');
  };

  const handleUpdateNews = async (id: number) => {
    setEditNewsLoading(true);
    try {
      const response = await launcherAPI.updateNews(id, {
        category: editCategory || undefined,
        description: editDescription || undefined,
      });
      if (response && response.success) {
        cancelEditNews();
        await fetchNews();
        await fetchFeed();
      } else {
        alert(response?.error ?? 'Failed to update news');
      }
    } catch (err) {
      console.error('Failed to update news:', err);
      alert('Failed to update news');
    } finally {
      setEditNewsLoading(false);
    }
  };

  const handleDeleteNews = async (id: number) => {
    if (!window.confirm('Delete this news item?')) return;
    try {
      const response = await launcherAPI.deleteNews(id);
      if (response && response.success) {
        setNews(prev => prev.filter(n => n.id !== id));
        setFeed(prev => prev.filter(f => !(f.type === 'news' && f.id === id)));
      } else {
        alert(response?.error ?? 'Failed to delete news');
      }
    } catch (err) {
      console.error('Failed to delete news:', err);
      alert('Failed to delete news');
    }
  };

  const getCategoryClass = (category: string): string => {
    switch (category?.toLowerCase()) {
      case 'event': return styles.categoryEvent;
      case 'announcement': return styles.categoryAnnouncement;
      case 'update': return styles.categoryUpdate;
      default: return styles.categoryDefault;
    }
  };

  useEffect(() => {
    fetchSignatures();
    fetchModels();
    fetchFeed();
    fetchNews();
    fetchChangelogs();
    fetchDiscordMembers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSignatures = async () => {
    setLoading(true);
    try {
      const response = await launcherAPI.getCheatSignatures();
      if (response && response.success && response.data) {
        setSignatures(response.data);
      } else {
        // Fallback to mock data if API fails
        setSignatures(mockSignatures);
      }
    } catch (err) {
      console.error('Failed to fetch signatures:', err);
      // Use mock data on error
      setSignatures(mockSignatures);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUploadFile(e.target.files[0]);
    }
  };

  const processSignatureFile = async () => {
    if (!uploadFile) return;

    setUploadProgress(true);
    try {
      const response = await launcherAPI.uploadSignatureFile(
        uploadFile,
        uploadSignatureType,
        uploadBlockType
      );

      if (response && response.success) {
        const d = response.data;
        alert(`Uploaded: ${d?.file_name ?? uploadFile.name} (hash: ${d?.file_hash ?? '—'})`);
        await fetchSignatures();
      } else {
        alert(response?.error ?? 'Failed to upload file');
      }

      setUploadFile(null);
      const fileInput = document.getElementById('signature-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file.');
    } finally {
      setUploadProgress(false);
    }
  };

  const deleteSignature = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this signature?')) return;

    try {
      const response = await launcherAPI.deleteCheatSignature(id);

      if (response && response.success) {
        setSignatures(prev => prev.filter(sig => sig.id !== id));
        alert('Signature deleted successfully');
      } else {
        alert('Failed to delete signature');
      }
    } catch (error) {
      console.error('Error deleting signature:', error);
      alert('Error deleting signature');
    }
  };

  const filteredSignatures = signatures.filter(sig => {
    const matchesSearch = sig.file_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         sig.file_hash.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         sig.signature_type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesBlockType = blockTypeFilter === 'all' || sig.block_type === blockTypeFilter;
    const matchesSignatureType = signatureTypeFilter === 'all' || sig.signature_type === signatureTypeFilter;
    return matchesSearch && matchesBlockType && matchesSignatureType;
  });

  const totalPages = Math.max(1, Math.ceil(filteredSignatures.length / ITEMS_PER_PAGE));
  const paginatedSignatures = filteredSignatures.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const getBlockTypeColor = (blockType: string) => {
    switch (blockType) {
      case 'blacklist': return '#ff4444';
      case 'whitelist': return '#28a745';
      default: return '#888';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (!bytes || bytes === 0) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Get unique signature types for filter
  const uniqueSignatureTypes = Array.from(
    new Set(signatures.map(sig => sig.signature_type))
  ).sort();

  // Manual Add Functions
  const handleManualAdd = async () => {
    if (!manualFileName || !manualFileHash) {
      alert('Please enter both file name and hash');
      return;
    }

    setManualAddLoading(true);
    try {
      const signature = {
        file_name: manualFileName,
        file_hash: manualFileHash,
        signature_type: manualSignatureType || 'file',
        block_type: manualBlockType,
        ...(manualFileSize !== '' && { file_size: parseInt(manualFileSize, 10) }),
      };

      const response = await launcherAPI.addCheatSignature(signature);

      if (response && response.success) {
        alert('Signature added successfully');
        // Clear form
        setManualFileName('');
        setManualFileHash('');
        setManualSignatureType('file');
        setManualBlockType('blacklist');
        setManualFileSize('');
        setShowManualAdd(false);
        // Refresh list
        await fetchSignatures();
      } else {
        alert('Failed to add signature');
      }
    } catch (error) {
      console.error('Error adding signature:', error);
      alert('Error adding signature');
    } finally {
      setManualAddLoading(false);
    }
  };

  // Sessions Functions
  const fetchSessions = async () => {
    setSessionsLoading(true);
    try {
      const response = await launcherAPI.getAllActiveSessions();
      if (response && response.success && response.data) {
        setSessions(response.data);
      } else {
        setSessions([]);
      }
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  };

  const requestScreenshot = async (sessionId: string) => {
    try {
      const response = await launcherAPI.getScreenshot(sessionId);
      if (response && response.success) {
        alert('Screenshot request sent to client');
      } else {
        alert('Failed to send screenshot request');
      }
    } catch (error) {
      console.error('Failed to request screenshot:', error);
    }
  };

  const forceDisconnect = async (sessionId: string) => {
    if (!window.confirm('Force disconnect this user?')) return;
    try {
      const response = await launcherAPI.forceUser(parseInt(sessionId, 10));
      if (response && response.success) {
        alert('User disconnected');
        fetchSessions();
      } else {
        alert('Failed to disconnect user');
      }
    } catch (error) {
      console.error('Failed to force disconnect:', error);
    }
  };

  // File Check Functions
  const checkFileUses = async () => {
    if (!fileName || !fileHash) {
      alert('Please enter both file name and hash');
      return;
    }

    setFileCheckLoading(true);
    try {
      const data = await launcherAPI.checkFileUses(fileName, fileHash);
      if (data) {
        setFileCheckResult(data);
      } else {
        setFileCheckResult(null);
        alert('No data returned for this file');
      }
    } catch (error) {
      console.error('Failed to check file:', error);
    } finally {
      setFileCheckLoading(false);
    }
  };

  // Screenshot Functions
  const fetchScreenshot = async () => {
    if (!screenshotSessionId) {
      alert('Please enter a session ID');
      return;
    }

    setScreenshotLoading(true);
    try {
      const data = await launcherAPI.takeScreenshot(screenshotSessionId);
      if (data) {
        const imgSrc = typeof data === 'string' ? data : (data.url ?? data.image ?? null);
        setScreenshotData(imgSrc);
      } else {
        setScreenshotData(null);
        alert('No screenshot data returned');
      }
    } catch (error) {
      console.error('Failed to fetch screenshot:', error);
    } finally {
      setScreenshotLoading(false);
    }
  };

  // Process Monitor Functions
  const fetchProcesses = async () => {
    if (!processSessionId) {
      alert('Please enter a session ID');
      return;
    }

    setProcessesLoading(true);
    try {
      const data = await launcherAPI.getActiveProcesses(processSessionId);
      if (Array.isArray(data)) {
        setProcesses(data);
      } else {
        setProcesses([]);
        alert('No process data returned');
      }
    } catch (error) {
      console.error('Failed to fetch processes:', error);
    } finally {
      setProcessesLoading(false);
    }
  };

  // Model Management Functions
  const fetchModels = async () => {
    setModelsLoading(true);
    setModelError(null);
    try {
      const response = await launcherAPI.listModels();
      if (response && response.success && Array.isArray(response.data)) {
        setModels(response.data);
      } else {
        setModels([]);
      }
    } catch (error) {
      console.error('Failed to fetch models:', error);
      setModelError('Failed to load models.');
    } finally {
      setModelsLoading(false);
    }
  };

  const handleModelFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setModelError(null);
    if (e.target.files && e.target.files[0]) {
      setModelUploadFile(e.target.files[0]);
    }
  };

  const handleModelUpload = async () => {
    if (!modelUploadFile) return;

    setModelUploadProgress(true);
    setModelError(null);
    try {
      const response = await launcherAPI.uploadModel(modelUploadFile);
      if (response && response.success && response.data) {
        setModelUploadFile(null);
        const fileInput = document.getElementById('model-upload') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
        await fetchModels();
      } else {
        const msg = response?.data?.error ?? response?.error ?? 'Upload failed.';
        setModelError(msg);
      }
    } catch (error) {
      console.error('Error uploading model:', error);
      setModelError('Upload failed.');
    } finally {
      setModelUploadProgress(false);
    }
  };

  const handleModelPreview = async (name: string) => {
    // If already previewing this model, close it
    if (modelPreview?.name === name) {
      URL.revokeObjectURL(modelPreview.url);
      setModelPreview(null);
      return;
    }
    // Revoke previous blob URL before creating a new one
    if (modelPreview) URL.revokeObjectURL(modelPreview.url);
    setModelError(null);
    try {
      const url = await launcherAPI.getModelImage(name);
      if (url) {
        setModelPreview({ name, url });
      } else {
        setModelError(`Could not load image for "${name}".`);
      }
    } catch (error) {
      console.error('Error loading model image:', error);
      setModelError('Failed to load image.');
    }
  };

  const handleModelDelete = async (name: string) => {
    if (!window.confirm(`Delete model "${name}"?`)) return;

    setModelError(null);
    try {
      const response = await launcherAPI.deleteModel(name);
      if (response && response.success) {
        setModels(prev => prev.filter(m => m.name !== name));
      } else {
        const msg = response?.data?.error ?? response?.error ?? 'Delete failed.';
        setModelError(msg);
      }
    } catch (error) {
      console.error('Error deleting model:', error);
      setModelError('Delete failed.');
    }
  };

  return (
    <Wrapper width="1200px">
      <Tabs defaultActiveKey="signatures">
        <TabPane tabTitle="Signatures" tabKey="signatures">

          {/* Upload Section */}
          <div className={styles.uploadSection}>
            <div className={styles.uploadHeader}>
              <h2 className={styles.sectionTitle}>Add Signatures</h2>
              <p className={styles.sectionSubtitle}>
                Upload a CSV file or add manually
              </p>
            </div>

            {/* File Upload */}
            <div className={styles.uploadControls}>
              <select
                className={styles.selectInput}
                value={uploadSignatureType}
                onChange={(e) => setUploadSignatureType(e.target.value)}
              >
                <option value="file">File</option>
                <option value="module">Module</option>
              </select>
              <select
                className={styles.selectInput}
                value={uploadBlockType}
                onChange={(e) => setUploadBlockType(e.target.value as 'blacklist' | 'whitelist')}
              >
                <option value="blacklist">Blacklist</option>
                <option value="whitelist">Whitelist</option>
              </select>
              <label htmlFor="signature-upload" className={styles.fileLabel}>
                <span className={styles.fileLabelIcon}>📁</span>
                {uploadFile ? uploadFile.name : 'Choose File'}
              </label>
              <input
                id="signature-upload"
                type="file"
                onChange={handleFileChange}
                className={styles.fileInput}
              />
              <button
                className={styles.btnUpload}
                onClick={processSignatureFile}
                disabled={!uploadFile || uploadProgress}
              >
                {uploadProgress ? 'Uploading...' : 'Upload File'}
              </button>
              <button
                className={styles.btnSecondary}
                onClick={() => setShowManualAdd(!showManualAdd)}
              >
                {showManualAdd ? 'Hide Manual Add' : '+ Add Manually'}
              </button>
            </div>

            <p className={styles.csvFormat}>
              Server calculates file name, hash and size automatically on upload.
            </p>

            {/* Manual Add Form */}
            {showManualAdd && (
              <div className={styles.manualAddForm}>
                <div className={styles.formRow}>
                  <div className={styles.inputGroup}>
                    <label className={styles.inputLabel}>File Name *</label>
                    <input
                      type="text"
                      className={styles.textInput}
                      placeholder="e.g., sobeit.dll"
                      value={manualFileName}
                      onChange={(e) => setManualFileName(e.target.value)}
                    />
                  </div>

                  <div className={styles.inputGroup}>
                    <label className={styles.inputLabel}>File Hash *</label>
                    <input
                      type="text"
                      className={styles.textInput}
                      placeholder="MD5/SHA256 hash"
                      value={manualFileHash}
                      onChange={(e) => setManualFileHash(e.target.value)}
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.inputGroup}>
                    <label className={styles.inputLabel}>Signature Type *</label>
                    <select
                      className={styles.selectInput}
                      value={manualSignatureType}
                      onChange={(e) => setManualSignatureType(e.target.value)}
                    >
                      <option value="file">File</option>
                      <option value="module">Module</option>
                    </select>
                  </div>

                  <div className={styles.inputGroup}>
                    <label className={styles.inputLabel}>Block Type *</label>
                    <select
                      className={styles.selectInput}
                      value={manualBlockType}
                      onChange={(e) => setManualBlockType(e.target.value as 'blacklist' | 'whitelist')}
                    >
                      <option value="blacklist">Blacklist</option>
                      <option value="whitelist">Whitelist</option>
                    </select>
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.inputGroup}>
                    <label className={styles.inputLabel}>File Size (bytes) <span className={styles.optionalHint}>optional</span></label>
                    <input
                      type="number"
                      min="0"
                      className={styles.textInput}
                      placeholder="e.g., 20480"
                      value={manualFileSize}
                      onChange={(e) => setManualFileSize(e.target.value)}
                    />
                  </div>
                </div>

                <button
                  className={styles.btnPrimary}
                  onClick={handleManualAdd}
                  disabled={manualAddLoading}
                >
                  {manualAddLoading ? 'Adding...' : 'Add Signature'}
                </button>
              </div>
            )}
          </div>

          {/* Filter Bar */}
          <div className={styles.filterBar}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search by name, hash, or type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              className={styles.selectInput}
              value={blockTypeFilter}
              onChange={(e) => setBlockTypeFilter(e.target.value)}
            >
              <option value="all">All Block Types</option>
              <option value="blacklist">Blacklist</option>
              <option value="whitelist">Whitelist</option>
            </select>
            <select
              className={styles.selectInput}
              value={signatureTypeFilter}
              onChange={(e) => setSignatureTypeFilter(e.target.value)}
            >
              <option value="all">All Signature Types</option>
              {uniqueSignatureTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <div className={styles.statsDisplay}>
              <span className={styles.statItem}>
                Total: <strong>{filteredSignatures.length}</strong>
              </span>
              <span className={styles.statItem} style={{ color: '#ff4444' }}>
                Blacklist: <strong>{signatures.filter(s => s.block_type === 'blacklist').length}</strong>
              </span>
              <span className={styles.statItem} style={{ color: '#28a745' }}>
                Whitelist: <strong>{signatures.filter(s => s.block_type === 'whitelist').length}</strong>
              </span>
            </div>
          </div>

          {/* Signatures List */}
          {loading ? (
            <div className={styles.loader}>
              <InfinitySpin width="200" color="#e7752e" />
            </div>
          ) : paginatedSignatures.length > 0 ? (
            <>
              <div className={styles.signatureGrid}>
                {paginatedSignatures.map((sig) => (
                  <div key={sig.id} className={styles.signatureCard}>
                    <div className={styles.cardHeader}>
                      <div className={styles.cardTitle}>
                        <span className={styles.signatureName}>{sig.file_name}</span>
                        <span
                          className={styles.severityBadge}
                          style={{ backgroundColor: getBlockTypeColor(sig.block_type) }}
                        >
                          {sig.block_type}
                        </span>
                      </div>
                      <button
                        className={styles.deleteBtn}
                        onClick={() => deleteSignature(sig.id)}
                        title="Delete signature"
                      >
                        ×
                      </button>
                    </div>

                    <div className={styles.cardBody}>
                      <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Hash:</span>
                        <code className={styles.infoValue}>{sig.file_hash}</code>
                      </div>
                      <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Type:</span>
                        <code className={styles.infoValue}>{sig.signature_type}</code>
                      </div>
                      <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Size:</span>
                        <span className={styles.infoValue}>{formatFileSize(sig.file_size)}</span>
                      </div>
                      <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Uploaded:</span>
                        <span className={styles.infoValue}>
                          {new Date(sig.upload_time).toLocaleDateString()} · ID {sig.uploaded_by}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className={styles.pagination}>
                  <button
                    className={styles.pageBtn}
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => p - 1)}
                  >
                    ← Prev
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((page) => {
                      if (totalPages <= 7) return true;
                      if (page === 1 || page === totalPages) return true;
                      return Math.abs(page - currentPage) <= 1;
                    })
                    .reduce<(number | string)[]>((acc, page, idx, arr) => {
                      if (idx > 0 && page - (arr[idx - 1] as number) > 1) acc.push('...');
                      acc.push(page);
                      return acc;
                    }, [])
                    .map((item, idx) =>
                      typeof item === 'string' ? (
                        <span key={`ellipsis-${idx}`} className={styles.pageEllipsis}>…</span>
                      ) : (
                        <button
                          key={item}
                          className={`${styles.pageBtn} ${currentPage === item ? styles.pageBtnActive : ''}`}
                          onClick={() => setCurrentPage(item)}
                        >
                          {item}
                        </button>
                      )
                    )}
                  <button
                    className={styles.pageBtn}
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(p => p + 1)}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>🔍</div>
              <div className={styles.emptyText}>No signatures found</div>
              <div className={styles.emptySubtext}>Upload a signature file to get started</div>
            </div>
          )}
        </TabPane>

        {/* Sessions Tab */}
        <TabPane tabTitle="Sessions" tabKey="sessions">
          <div className={styles.actionSection}>
            <h2 className={styles.sectionTitle}>Active Launcher Sessions</h2>
            <button className={styles.btnPrimary} onClick={fetchSessions}>
              Refresh Sessions
            </button>
          </div>

          {sessionsLoading ? (
            <div className={styles.loader}>
              <InfinitySpin width="200" color="#e7752e" />
            </div>
          ) : sessions.length > 0 ? (
            <div className={styles.sessionGrid}>
              {sessions.map((session) => (
                <div key={session.id} className={styles.sessionCard}>
                  <div className={styles.sessionHeader}>
                    <span className={styles.sessionUser}>{session.username}</span>
                    <span
                      className={styles.statusBadge}
                      style={{
                        backgroundColor: session.status === 'active' ? '#28a745' :
                                       session.status === 'idle' ? '#ffc107' : '#dc3545'
                      }}
                    >
                      {session.status}
                    </span>
                  </div>

                  <div className={styles.sessionBody}>
                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>Session ID:</span>
                      <code className={styles.infoValue}>{session.id}</code>
                    </div>
                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>HWID:</span>
                      <code className={styles.infoValue}>{session.hwid}</code>
                    </div>
                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>IP Address:</span>
                      <code className={styles.infoValue}>{session.ipAddress}</code>
                    </div>
                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>Connected:</span>
                      <span className={styles.infoValue}>{session.connectedAt}</span>
                    </div>
                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>Last Activity:</span>
                      <span className={styles.infoValue}>{session.lastActivity}</span>
                    </div>
                  </div>

                  <div className={styles.sessionActions}>
                    <button
                      className={styles.btnSecondary}
                      onClick={() => requestScreenshot(session.id)}
                    >
                      📸 Screenshot
                    </button>
                    <button
                      className={styles.btnDanger}
                      onClick={() => forceDisconnect(session.id)}
                    >
                      🚫 Disconnect
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>💤</div>
              <div className={styles.emptyText}>No active sessions</div>
              <div className={styles.emptySubtext}>Click refresh to load sessions</div>
            </div>
          )}
        </TabPane>

        {/* File Monitoring Tab */}
        <TabPane tabTitle="File Monitor" tabKey="files">
          <div className={styles.actionSection}>
            <h2 className={styles.sectionTitle}>Check File Usage</h2>
            <p className={styles.sectionSubtitle}>
              Monitor how many times a specific file hash has been detected across clients
            </p>
          </div>

          <div className={styles.fileCheckSection}>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>File Name</label>
              <input
                type="text"
                className={styles.textInput}
                placeholder="e.g., sobeit.dll"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
              />
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>File Hash (MD5/SHA256)</label>
              <input
                type="text"
                className={styles.textInput}
                placeholder="e.g., a1b2c3d4e5f6..."
                value={fileHash}
                onChange={(e) => setFileHash(e.target.value)}
              />
            </div>

            <button
              className={styles.btnPrimary}
              onClick={checkFileUses}
              disabled={fileCheckLoading}
            >
              {fileCheckLoading ? 'Checking...' : 'Check File'}
            </button>
          </div>

          {fileCheckResult && (
            <div className={`${styles.resultCard} ${fileCheckResult.flagged ? styles.flagged : ''}`}>
              <h3 className={styles.resultTitle}>
                {fileCheckResult.flagged ? '⚠️ Flagged File' : '✓ File Check Result'}
              </h3>
              <div className={styles.resultBody}>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>File Name:</span>
                  <code className={styles.infoValue}>{fileCheckResult.name}</code>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Hash:</span>
                  <code className={styles.infoValue}>{fileCheckResult.hash}</code>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Usage Count:</span>
                  <span className={styles.infoValue}>
                    <strong>{fileCheckResult.usageCount}</strong> detections
                  </span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Last Seen:</span>
                  <span className={styles.infoValue}>
                    {new Date(fileCheckResult.lastSeen).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          )}
        </TabPane>

        {/* Screenshots Tab */}
        <TabPane tabTitle="Screenshots" tabKey="screenshots">
          <div className={styles.actionSection}>
            <h2 className={styles.sectionTitle}>Client Screenshots</h2>
            <p className={styles.sectionSubtitle}>
              Request and view screenshots from active launcher sessions
            </p>
          </div>

          <div className={styles.screenshotSection}>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>Session ID</label>
              <input
                type="text"
                className={styles.textInput}
                placeholder="e.g., sess_001"
                value={screenshotSessionId}
                onChange={(e) => setScreenshotSessionId(e.target.value)}
              />
            </div>

            <button
              className={styles.btnPrimary}
              onClick={fetchScreenshot}
              disabled={screenshotLoading}
            >
              {screenshotLoading ? 'Loading...' : 'Get Screenshot'}
            </button>
          </div>

          {screenshotData && (
            <div className={styles.screenshotPreview}>
              <h3 className={styles.previewTitle}>Screenshot Preview</h3>
              <img
                src={screenshotData}
                alt="Client Screenshot"
                className={styles.screenshotImage}
              />
              <div className={styles.screenshotActions}>
                <button
                  className={styles.btnSecondary}
                  onClick={() => window.open(screenshotData, '_blank')}
                >
                  Open Full Size
                </button>
              </div>
            </div>
          )}
        </TabPane>

        {/* Process Monitor Tab */}
        <TabPane tabTitle="Processes" tabKey="processes">
          <div className={styles.actionSection}>
            <h2 className={styles.sectionTitle}>Process Monitor</h2>
            <p className={styles.sectionSubtitle}>
              View active processes running on client machines
            </p>
          </div>

          <div className={styles.processSection}>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>Session ID</label>
              <input
                type="text"
                className={styles.textInput}
                placeholder="e.g., sess_001"
                value={processSessionId}
                onChange={(e) => setProcessSessionId(e.target.value)}
              />
            </div>

            <button
              className={styles.btnPrimary}
              onClick={fetchProcesses}
              disabled={processesLoading}
            >
              {processesLoading ? 'Loading...' : 'Get Processes'}
            </button>
          </div>

          {processes.length > 0 && (
            <div className={styles.processTable}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Process Name</th>
                    <th>PID</th>
                    <th>Path</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {processes.map((proc, index) => (
                    <tr key={index} className={proc.flagged ? styles.flaggedRow : ''}>
                      <td>
                        <strong>{proc.name}</strong>
                      </td>
                      <td>
                        <code>{proc.pid}</code>
                      </td>
                      <td>
                        <code className={styles.pathText}>{proc.path}</code>
                      </td>
                      <td>
                        {proc.flagged ? (
                          <span className={styles.flagBadge}>⚠️ FLAGGED</span>
                        ) : (
                          <span className={styles.safeBadge}>✓ Safe</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabPane>
        {/* Models Tab */}
        <TabPane tabTitle="Models" tabKey="models">
          <div className={styles.uploadSection}>
            <div className={styles.uploadHeader}>
              <h2 className={styles.sectionTitle}>Anti-Cheat Models</h2>
              <p className={styles.sectionSubtitle}>
                Upload and manage image models used by the anti-cheat system. Accepted formats: jpg, jpeg, png.
              </p>
            </div>

            <div className={styles.uploadControls}>
              <label htmlFor="model-upload" className={styles.fileLabel}>
                <span className={styles.fileLabelIcon}>📁</span>
                {modelUploadFile ? modelUploadFile.name : 'Choose Image'}
              </label>
              <input
                id="model-upload"
                type="file"
                accept=".jpg,.jpeg,.png"
                onChange={handleModelFileChange}
                className={styles.fileInput}
              />
              <button
                className={styles.btnUpload}
                onClick={handleModelUpload}
                disabled={!modelUploadFile || modelUploadProgress}
              >
                {modelUploadProgress ? 'Uploading...' : 'Upload Model'}
              </button>
              <button
                className={styles.btnSecondary}
                onClick={fetchModels}
                disabled={modelsLoading}
              >
                ↻ Refresh
              </button>
            </div>

            {modelError && (
              <p className={styles.csvFormat} style={{ color: '#dc3545', marginTop: 8 }}>
                {modelError}
              </p>
            )}
          </div>

          {modelsLoading ? (
            <div className={styles.loader}>
              <InfinitySpin width="200" color="#e7752e" />
            </div>
          ) : models.length > 0 ? (
            <div className={styles.processTable}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>File Name</th>
                    <th>Size</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {models.map((model) => (
                    <tr key={model.name}>
                      <td><strong>{model.name}</strong></td>
                      <td>{formatFileSize(model.size)}</td>
                      <td style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <button
                          className={styles.btnSecondary}
                          style={{ padding: '3px 10px', fontSize: '0.8rem' }}
                          onClick={() => handleModelPreview(model.name)}
                          title={`Preview ${model.name}`}
                        >
                          {modelPreview?.name === model.name ? 'Hide' : 'View'}
                        </button>
                        <button
                          className={styles.deleteBtn}
                          onClick={() => handleModelDelete(model.name)}
                          title={`Delete ${model.name}`}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>🖼️</div>
              <div className={styles.emptyText}>No models uploaded</div>
              <div className={styles.emptySubtext}>Upload a jpg, jpeg, or png to get started</div>
            </div>
          )}

          {modelPreview && (
            <div className={styles.screenshotPreview}>
              <h3 className={styles.previewTitle}>{modelPreview.name}</h3>
              <img
                src={modelPreview.url}
                alt={modelPreview.name}
                className={styles.screenshotImage}
              />
              <div className={styles.screenshotActions}>
                <button
                  className={styles.btnSecondary}
                  onClick={() => window.open(modelPreview.url, '_blank')}
                >
                  Open Full Size
                </button>
                <button
                  className={styles.btnSecondary}
                  onClick={() => { URL.revokeObjectURL(modelPreview.url); setModelPreview(null); }}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </TabPane>
        {/* Feed & News Tab */}
        <TabPane tabTitle="Feed & News" tabKey="feed-news">

          {/* Discord Members + header */}
          <div className={styles.feedHeader}>
            <div className={styles.discordStat}>
              <span className={styles.discordIcon}>💬</span>
              <div>
                <div className={styles.discordCount}>
                  {discordMembers !== null ? discordMembers.toLocaleString() : '—'}
                </div>
                <div className={styles.discordLabel}>Discord Members</div>
              </div>
            </div>
            <button
              className={styles.btnSecondary}
              onClick={fetchDiscordMembers}
              style={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.15)' }}
            >
              ↻ Refresh
            </button>
          </div>

          {/* Post News Form */}
          <div className={styles.postNewsForm}>
            <h2 className={styles.sectionTitle} style={{ marginBottom: 16 }}>Post News</h2>
            <div className={styles.postNewsControls}>
              <div className={styles.inputGroup} style={{ flex: '0 0 180px' }}>
                <label className={styles.inputLabel}>Category</label>
                <select
                  className={styles.selectInput}
                  value={newsCategory}
                  onChange={(e) => setNewsCategory(e.target.value)}
                >
                  <option value="announcement">Announcement</option>
                  <option value="event">Event</option>
                  <option value="update">Update</option>
                </select>
              </div>
              <div className={styles.inputGroup} style={{ flex: 1 }}>
                <label className={styles.inputLabel}>Description</label>
                <textarea
                  className={styles.textareaInput}
                  placeholder="Enter news description..."
                  value={newsDescription}
                  onChange={(e) => setNewsDescription(e.target.value)}
                />
              </div>
              <button
                className={styles.btnPrimary}
                onClick={handlePostNews}
                disabled={postNewsLoading || !newsDescription.trim()}
                style={{ alignSelf: 'flex-end' }}
              >
                {postNewsLoading ? 'Posting...' : 'Post'}
              </button>
            </div>
          </div>

          {/* Latest Feed */}
          <div className={styles.feedSection}>
            <p className={styles.feedSectionTitle}>Latest Activity</p>
            {feedLoading ? (
              <div className={styles.loader}><InfinitySpin width="200" color="#e7752e" /></div>
            ) : feed.length > 0 ? (
              <div className={styles.feedList}>
                {feed.map((item) => (
                  <div key={`${item.type}-${item.id}`}>
                    <div className={`${styles.feedItem} ${item.type === 'news' ? styles.feedItemTypeNews : styles.feedItemTypeChangelog}`}>
                      <span className={`${styles.feedTypeBadge} ${item.type === 'news' ? styles.feedTypeBadgeNews : styles.feedTypeBadgeChangelog}`}>
                        {item.type}
                      </span>
                      <div className={styles.feedItemContent}>
                        <p className={styles.feedItemText}>
                          {item.version && <><strong>{item.version}</strong> — </>}
                          {item.text.split('\n')[0].replace(/\*\*/g, '').substring(0, 120)}
                          {item.text.length > 120 ? '…' : ''}
                        </p>
                        <div className={styles.feedItemMeta}>
                          {item.category && (
                            <span className={`${styles.categoryBadge} ${getCategoryClass(item.category)}`}>
                              {item.category}
                            </span>
                          )}
                          <span className={styles.feedItemTime}>
                            {new Date(item.timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        </div>
                      </div>
                      {item.type === 'news' && (
                        <div className={styles.feedItemActions}>
                          <button
                            className={styles.feedEditBtn}
                            title="Edit"
                            onClick={() => editingNewsId === item.id ? cancelEditNews() : startEditNews(item.id, item.category ?? '', item.text)}
                          >✏</button>
                          <button
                            className={styles.feedDeleteBtn}
                            title="Delete"
                            onClick={() => handleDeleteNews(item.id)}
                          >×</button>
                        </div>
                      )}
                    </div>
                    {item.type === 'news' && editingNewsId === item.id && (
                      <div className={styles.feedEditInline}>
                        <div className={styles.feedEditRow}>
                          <div className={styles.inputGroup} style={{ flex: '0 0 160px' }}>
                            <label className={styles.inputLabel}>Category</label>
                            <select className={styles.selectInput} value={editCategory} onChange={(e) => setEditCategory(e.target.value)}>
                              <option value="announcement">Announcement</option>
                              <option value="event">Event</option>
                              <option value="update">Update</option>
                            </select>
                          </div>
                          <div className={styles.inputGroup} style={{ flex: 1 }}>
                            <label className={styles.inputLabel}>Description</label>
                            <textarea
                              className={styles.textareaInput}
                              value={editDescription}
                              onChange={(e) => setEditDescription(e.target.value)}
                              style={{ minHeight: 60 }}
                            />
                          </div>
                          <div className={styles.feedEditActions}>
                            <button className={styles.btnPrimary} onClick={() => handleUpdateNews(item.id)} disabled={editNewsLoading || !editDescription.trim()}>
                              {editNewsLoading ? '…' : 'Save'}
                            </button>
                            <button className={styles.btnSecondary} onClick={cancelEditNews}>Cancel</button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState} style={{ padding: '20px 0' }}>
                <div className={styles.emptyText} style={{ fontSize: '0.9rem' }}>No recent activity</div>
              </div>
            )}
          </div>

          {/* News */}
          <div className={styles.feedSection}>
            <div className={styles.feedSectionHeader}>
              <p className={styles.feedSectionTitle} style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>News</p>
              <button className={styles.btnSecondary} onClick={fetchNews} style={{ padding: '4px 12px', fontSize: '0.8rem' }}>↻ Refresh</button>
            </div>
            {newsLoading ? (
              <div className={styles.loader}><InfinitySpin width="200" color="#e7752e" /></div>
            ) : news.length > 0 ? (
              <div className={styles.newsGrid}>
                {news.map((item) => (
                  <div key={item.id} className={`${styles.newsCard} ${editingNewsId === item.id ? styles.newsCardEditing : ''}`}>
                    {editingNewsId === item.id ? (
                      <div className={styles.newsEditForm}>
                        <div className={styles.inputGroup}>
                          <label className={styles.inputLabel}>Category</label>
                          <select className={styles.selectInput} value={editCategory} onChange={(e) => setEditCategory(e.target.value)}>
                            <option value="announcement">Announcement</option>
                            <option value="event">Event</option>
                            <option value="update">Update</option>
                          </select>
                        </div>
                        <div className={styles.inputGroup} style={{ marginTop: 10 }}>
                          <label className={styles.inputLabel}>Description</label>
                          <textarea
                            className={styles.textareaInput}
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            style={{ minHeight: 72 }}
                          />
                        </div>
                        <div className={styles.newsEditFormActions}>
                          <button className={styles.btnPrimary} onClick={() => handleUpdateNews(item.id)} disabled={editNewsLoading || !editDescription.trim()}>
                            {editNewsLoading ? 'Saving…' : 'Save'}
                          </button>
                          <button className={styles.btnSecondary} onClick={cancelEditNews}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className={styles.newsCardHeader}>
                          <span className={`${styles.categoryBadge} ${getCategoryClass(item.category)}`}>
                            {item.category}
                          </span>
                          <div className={styles.newsCardActions}>
                            <span className={styles.newsCardTime}>
                              {new Date(item.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                            <button className={styles.newsEditBtn} title="Edit" onClick={() => startEditNews(item.id, item.category, item.description)}>✏</button>
                            <button className={styles.newsDeleteBtn} title="Delete" onClick={() => handleDeleteNews(item.id)}>×</button>
                          </div>
                        </div>
                        <div className={styles.newsCardBody}>{item.description}</div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState} style={{ padding: '20px 0' }}>
                <div className={styles.emptyText} style={{ fontSize: '0.9rem' }}>No news items</div>
              </div>
            )}
          </div>

          {/* Changelogs */}
          <div className={styles.feedSection}>
            <div className={styles.feedSectionHeader}>
              <p className={styles.feedSectionTitle} style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>Changelogs</p>
              <button className={styles.btnSecondary} onClick={fetchChangelogs} style={{ padding: '4px 12px', fontSize: '0.8rem' }}>↻ Refresh</button>
            </div>
            {changelogsLoading ? (
              <div className={styles.loader}><InfinitySpin width="200" color="#e7752e" /></div>
            ) : changelogs.length > 0 ? (
              <div className={styles.changelogList}>
                {changelogs.map((item) => (
                  <div key={item.id} className={styles.changelogCard}>
                    <div className={styles.changelogHeader}>
                      <span className={styles.changelogVersion}>{item.version}</span>
                      <span className={styles.changelogTime}>
                        {new Date(item.discord_timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                    <div className={styles.changelogText}>
                      {item.text.replace(/\*\*/g, '').replace(/~w~/g, '')}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState} style={{ padding: '20px 0' }}>
                <div className={styles.emptyText} style={{ fontSize: '0.9rem' }}>No changelogs</div>
              </div>
            )}
          </div>

        </TabPane>
      </Tabs>
    </Wrapper>
  );
};

export default ManagementLauncher;
